import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import { useProjectPipelines } from "@/hooks/useProjectPipelines";
import {
  extractStoryRefs,
  aggregateBySkill,
  type SkillCostAggregate,
  type StoryCostSummary,
} from "@/lib/storyRefs";

export type { StoryCostSummary } from "@/lib/storyRefs";

export interface StoryCostRow {
  ref: string;
  data: StoryCostSummary | null;
  loading: boolean;
}

export interface UseStoryCostsResult {
  storyCosts: StoryCostRow[];
  skillCosts: SkillCostAggregate[];
  settledCount: number;
  totalRefs: number;
  loading: boolean;
  error: string;
}

/**
 * Fan-out hook: enumerates story refs from pipeline runs' issue_id,
 * then queries each ref's cost summary (SD-3, SD-5, D10).
 * 404 responses are treated as data (null), not errors.
 */
export function useStoryCosts(
  projectSlug: string | null,
): UseStoryCostsResult {
  const {
    runs,
    loading: pipelinesLoading,
    error: pipelinesError,
  } = useProjectPipelines(projectSlug);

  // SD-5: memoize refs on content key to keep useQueries stable across 10s polls
  const { refs, totalRefs } = useMemo(() => extractStoryRefs(runs), [runs]);
  const refsKey = refs.join(",");
  const stableRefs = useMemo(() => refs, [refsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryResults = useQueries({
    queries: stableRefs.map((ref) => ({
      queryKey: queryKeys.metrics.storyCost(ref),
      queryFn: async (): Promise<StoryCostSummary | null> => {
        try {
          return await api<StoryCostSummary>(
            `/api/v1/telemetry/story-cost?story=${encodeURIComponent(ref)}`,
          );
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            return null;
          }
          throw err;
        }
      },
      staleTime: 300_000,
      retry: false,
    })),
  });

  const storyCosts: StoryCostRow[] = stableRefs.map((ref, i) => ({
    ref,
    data: queryResults[i]?.data ?? null,
    loading: queryResults[i]?.isLoading ?? true,
  }));

  const settledResults = queryResults
    .filter((q) => q.isSuccess)
    .map((q) => q.data ?? null);

  const settledCount = settledResults.filter((r) => r !== null).length;
  const settledTotal = settledResults.length;

  const skillCosts = useMemo(
    () => aggregateBySkill(settledResults),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settledTotal, settledCount],
  );

  return {
    storyCosts,
    skillCosts,
    settledCount,
    totalRefs,
    loading: projectSlug !== null && pipelinesLoading,
    error: pipelinesError,
  };
}
